import { Num } from '../../../../utils/common'
import { DOMUtils } from '../../../../utils/dom'
import { Mixin } from '../../../../utils/mixin'
import {
    IIdentityProviderConfig,
    IIdentityServiceAdapter,
    ISubscriberProviderConfig,
    ISubscriberServiceAdapter,
    ISubscription,
    SSOScreen
} from '../adapter'

interface IPianoApiResponse {
    code: number
    count?: number
    data?: object[]
    limit: number
    offset: number
    total: number
}

interface IPianoApiAccessItem {
    expire_date?: number
    resource: {
        rid: string
    }
    start_date: number
}

interface IWindow {
    janrain: any
    janrainCaptureWidgetOnLoad: () => void
    tp: any
}

declare let window: IWindow

export const WithPiano = <T extends Mixin<IIdentityServiceAdapter<IIdentityProviderConfig>>>(Base: T) =>
    class extends Base implements ISubscriberServiceAdapter {
        public subscriberWhitelist: string[]
        private endpoints = {
            access: '/access/list',
            verify: '/anon/janrain/verifyAccountInfo'
        }
        private pianoJSUrl = '//cdn.tinypass.com/api/tinypass.min.js'
        private ready: Promise<boolean>
        private tpCallApi: (endpoint: string, data: any, callback: (res: any) => void) => void
        private verified: Promise<boolean>

        public initialize(conf: ISubscriberProviderConfig, ssoCreds: IIdentityProviderConfig) {
            super.initialize(ssoCreds)

            if (!conf) {
                return this
            }

            this.subscriberWhitelist = conf.resIds

            /**
             * This may or may not be required in the future, but for sake of migrations,
             * we're not initializing when the global object is already present
             * (meaning the backend has presumably configured it loaded the js).
             * We still add the init handler, but don't perform the SSO integration.
             */
            if (!!window.tp) {
                this.ready = new Promise((resolve) =>
                    window.tp.push(['init', () => this.onInit(ssoCreds, resolve)]))
                return this
            }

             // setup global object
            window.tp = window.tp || []

            // configure (promised as SSO session found event can happen before Piano init)
            this.ready = new Promise((resolve) =>
                window.tp.push(['init', () => this.onInit(ssoCreds, resolve)]))
            window.tp.push(['setAid', conf.account])

            // dev/stage only
            if (__NON_PROD) {
                window.tp.push(['setDebug', true])
                // for sandbox:
                // window.tp.push(['setSandbox', true])
                // this.pianoJSUrl = '//sandbox.tinypass.com/api/tinypass.min.js'
            }

            // load JS
            DOMUtils.loadScript(this.pianoJSUrl)

            return this
        }

        /**
         * Fetches user subscriptions.
         */
        public async getAccess(): Promise<ISubscription[]> {
            await this.verified
            const res = await this.callApi(this.endpoints.access)
            // TODO: catch rejects - retry logic? default to `false`?
            return (res.data || []).map((item: IPianoApiAccessItem) => {
                const sub = {
                    id: item.resource.rid,
                    start: item.start_date * Num.secToMs
                } as ISubscription

                // expiry not always present
                if (item.expire_date) {
                    sub.end = item.expire_date * Num.secToMs
                }

                return sub
            })
        }

        public registerInitHandler() {
            // override Janrain as Piano handles this
        }

        /**
         * Calls Piano API via global object.
         */
        private async callApi(endpoint: string, data?: any): Promise<IPianoApiResponse> {
            await this.ready
            return new Promise((resolve, reject) => {
                try {
                    window.tp.api.callApi(endpoint, data, (res: IPianoApiResponse) => {
                        if (res.code > 0) {
                            reject(new Error(`Piano API failure: ${JSON.stringify(res)}`))
                        } else {
                            resolve(res)
                        }
                    })
                } catch (e) {
                    reject(e)
                }
            })
        }

        private onInit(ssoCreds: IIdentityProviderConfig, resolve: () => void) {
            if (ssoCreds) {
                window.tp.janrain.init({
                    appId: ssoCreds.account,
                    appName: ssoCreds.application,
                    clientId: ssoCreds.client,
                    loginRequiredScreen: SSOScreen.SIGN_IN,
                })
            }

            // now we can enable Janrain adapter
            if (window.janrain.events) {
                super.enable()
            } else {
                const existingHandler = window.janrainCaptureWidgetOnLoad
                window.janrainCaptureWidgetOnLoad = () => {
                    super.enable()
                    if (existingHandler) {
                        existingHandler()
                    }
                }
            }

            this.watchForSSOVerification()

            resolve()
        }

        /**
         * Watch for Piano SSO verification to complete.
         * NOTE: workaround for lack of 'api ready' event.
         */
        private watchForSSOVerification(): void {
            this.verified = new Promise((resolve, reject) => {

                // wrap `callApi` to intercept verification response
                if (!this.tpCallApi) {
                    this.tpCallApi = window.tp.api.callApi
                }
                window.tp.api.callApi = (endpoint: string, data: any, callback: (res: any) => void) => {
                    this.tpCallApi(endpoint, data, (res: any) => {
                        callback(res)
                        if (endpoint === this.endpoints.verify) {
                            res.code === 0 ? resolve() : reject()
                        }
                    })
                }

                const existingHandler = window.janrainCaptureWidgetOnLoad
                window.janrainCaptureWidgetOnLoad = () => {

                    // need to watch again if user signs out
                    window.janrain.events.onCaptureSessionEnded.addHandler(this.watchForSSOVerification.bind(this))

                    // session exists, no need to wait
                    window.janrain.events.onCaptureSessionFound.addHandler(() => resolve())

                    if (existingHandler) {
                        existingHandler()
                    }
                }
            })
        }
    }
